import { Component, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import MobileCtaBar from "@/components/MobileCtaBar";
import Analytics from "@/components/Analytics";
import MentionsLegales from "@/pages/MentionsLegales";
import CGV from "@/pages/CGV";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import Nav from "@/components/Nav";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import ProblemSolution from "@/components/ProblemSolution";
import Services from "@/components/Services";
import WhyUs from "@/components/WhyUs";
import BeforeAfter from "@/components/BeforeAfter";
import HowItWorks from "@/components/HowItWorks";
import Pricing from "@/components/Pricing";
import Buyback from "@/components/Buyback";
import Boutique from "@/components/Boutique";
import Refurbished from "@/components/Refurbished";
import Reviews from "@/components/Reviews";
import InstagramBanner from "@/components/InstagramBanner";
import LocalZone from "@/components/LocalZone";
import Faq from "@/components/Faq";
import QuoteForm from "@/components/QuoteForm";
import Footer from "@/components/Footer";
import AuthCallback from "@/components/AuthCallback";
import Profile from "@/pages/Profile";
import ResetPassword from "@/pages/ResetPassword";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-night p-8 text-center">
          <p className="text-slate-400">Une erreur est survenue. Rechargez la page.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function useLenis() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function Landing() {
  useLenis();
  return (
    <div className="min-h-screen bg-night text-white">
      <div className="noise-overlay" aria-hidden="true" />
      <Nav />
      <main>
        <Hero />
        <Marquee />
        <ProblemSolution />
        <Services />
        <WhyUs />
        <BeforeAfter />
        <HowItWorks />
        <Pricing />
        <Buyback />
        <Boutique />
        <Refurbished />
        <Reviews />
        <InstagramBanner />
        <LocalZone />
        <Faq />
        <QuoteForm />
      </main>
      <Footer />
      <MobileCtaBar />
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  // Process OAuth callback synchronously during render (before auth check effects)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/profil" element={<Profile />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/mentions-legales" element={<MentionsLegales />} />
        <Route path="/cgv" element={<CGV />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Analytics />
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <AppRoutes />
          <Toaster theme="light" position="top-center" toastOptions={{ style: { background: "#FFFFFF", border: "1px solid #BAE6FD", color: "#0F172A" } }} />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
